import React from 'react';
import {
  booleanOptions2,
  ConditionType,
  Filter,
  FilterOption,
  getCondition,
  getRelationalExpressionProps,
  isNullOperator,
  RelationsProps,
  Select,
  selectorWidth,
  StringConditionType,
} from '@fabric-ui/components';
import { useGetValuesOptions } from '../../views/Logs/hooks';
import FilterInput from './components/FilterInput';
import { useExpressionBuilder } from './ExpressionBuilderContext';
import { useFilterConfig } from './FilterContext';

const emptyRelationsProps: RelationsProps = {};

interface StringNumberTypeBoxProps {
  filter: Filter;
  setFilter: any;
  typeCondition: ConditionType;
  options: FilterOption[];
  disabled?: boolean;
  relationsProps?: RelationsProps;
  resetOperatorOnChange?: boolean;
  filterInputProps?: Record<string, any>;
}
const FilterTypeBox: React.FC<StringNumberTypeBoxProps> = ({
  filter,
  setFilter,
  typeCondition,
  options,
  disabled = false,
  relationsProps = emptyRelationsProps,
  resetOperatorOnChange = false,
  filterInputProps,
}) => {
  const { size, isVertical } = useFilterConfig();
  const { supportParser, timeRange, readOnly, tokenSuggestions } = useExpressionBuilder();
  const compoundOperator = getCondition(filter, typeCondition, supportParser ? StringConditionType.EQUALS : undefined);
  const [operator] = compoundOperator.split('|');
  const { additionalSelectOptions = [], enableCaseSensitive = false, enableSide = false } = relationsProps;

  const innerExpression = filter.expression?.arrayCondition?.underlying || filter.expression;

  const setValue = value => {
    const wasToken = resetOperatorOnChange;
    const firstOption = options[0]?.value ?? operator;
    const [firstBaseOperator, firstResolution] = firstOption.split('|');
    const effectiveOperator = wasToken ? firstBaseOperator : operator;
    const underlying = {
      fieldName: innerExpression?.fieldName,
      [typeCondition]: { [effectiveOperator]: value },
      ...getRelationalExpressionProps({ operator: effectiveOperator, typeCondition, enableCaseSensitive, enableSide }),
    };
    const existingResolution = filter.expression?.arrayCondition?.resolution;
    const newResolution = wasToken && firstResolution ? firstResolution : existingResolution;
    return setFilter({
      ...filter,
      expression: filter.expression?.arrayCondition ? { arrayCondition: { underlying, resolution: newResolution } } : underlying,
    });
  };

  const value = isNullOperator(operator) ? '' : innerExpression?.[typeCondition]?.[operator];

  const width = isVertical ? selectorWidth : 250;
  const isInputDisabled = isNullOperator(operator) || !innerExpression?.fieldName || disabled;

  const inputProps = {
    readOnly,
    disabled: isInputDisabled,
    value,
    onChange: setValue,
    placeholder: isInputDisabled ? '' : 'Type Value',
    size,
  };

  const enableAutoCompleteValue =
    !!supportParser &&
    [StringConditionType.EQUALS, StringConditionType.NOT_EQUAL].includes(operator as StringConditionType) &&
    !!innerExpression?.fieldName;
  const { data: openSelectOptions, isLoading } = useGetValuesOptions({
    fieldName: innerExpression?.fieldName,
    enabled: enableAutoCompleteValue,
    timeRange,
  });
  return [ConditionType.bool, ConditionType.field /* , ConditionType.enum */].includes(typeCondition) ? (
    <Select
      {...inputProps}
      style={{ width }}
      isRequired
      muiProps={{ disablePortal: true }}
      options={[
        ...(typeCondition === ConditionType.bool ? booleanOptions2 : additionalSelectOptions),
        ...(isNullOperator(operator) ? [{ title: '', value: '' }] : []),
      ]}
    />
  ) : (
    <FilterInput
      {...inputProps}
      inputWidth={width}
      numberField={typeCondition === ConditionType.number}
      operator={compoundOperator}
      options={options}
      optionsUseQueryProps={enableAutoCompleteValue ? { options: openSelectOptions, isLoading } : undefined}
      skeletonLoading
      tokenSuggestions={tokenSuggestions}
      {...filterInputProps}
    />
  );
};

export default FilterTypeBox;
